// Complete replacement asset. Uses the original cart-total eligibility rule.
(() => {
  if (window.cartGiftController) return;
  class CartGiftController {
    isBusy() {
      return Boolean(this.intent || this.requests || this.running || this.pending || this.timer || this.failed);
    }
    updateNavigation() {
      const busy = this.isBusy();
      document.querySelectorAll('cart-drawer a[href], cart-drawer button[name="checkout"]').forEach(element => {
        if (element.tagName === 'A' && !/\/cart\/?$/.test(new URL(element.href, location.href).pathname)) return;
        if (busy) {
          if (!element.hasAttribute('data-gift-navigation-lock')) {
            element.dataset.giftNavigationLock = element.getAttribute('aria-disabled') || '';
          }
          element.setAttribute('aria-disabled', 'true');
          element.setAttribute('aria-busy', 'true');
        } else if (element.hasAttribute('data-gift-navigation-lock')) {
          const previous = element.dataset.giftNavigationLock;
          if (previous) element.setAttribute('aria-disabled', previous);
          else element.removeAttribute('aria-disabled');
          element.removeAttribute('aria-busy');
          delete element.dataset.giftNavigationLock;
        }
      });
    }
    readConfig() {
      const element = document.querySelector('drawer-gifts');
      const fallback = document.getElementById('cart-gifts-config');
      const config = JSON.parse(element?.dataset.config || fallback?.textContent || '{"data":{"gifts":[]}}').data;
      return { gifts: (config.gifts || []).map(gift => ({
        ...gift,
        price: Number(gift.price) * 100,
        product_id: Number(gift.product_id),
        variant_ids: (gift.variant_ids || [gift.product_id]).map(Number)
      })).filter(gift => gift.price > 0 && gift.product_id > 0) };
    }
    async request(path, body) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await (this.originalFetch || window.fetch.bind(window))(`${root}${path}`, body ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to update your gift.');
      return data;
    }


    async sync() {
      const config = this.readConfig();
      let cart = await this.request('cart.js');
      let changed = false;
      // Re-read the cart after every mutation. Both rewards settle before rendering.
      // A bound avoids endless retries if external cart logic conflicts with gifts.
      for (let pass = 0; pass < 12; pass += 1) {
        let action = null;
        for (const gift of config.gifts) {
          const matches = (cart.items || []).filter((item) =>
            gift.variant_ids.includes(Number(item.variant_id || item.id)) &&
            item.properties?.Gift === 'Free Gift');
          const quantity = matches.reduce((sum, item) => sum + item.quantity, 0);
          const eligible = cart.total_price >= gift.price;
          if (!eligible && quantity) {
            action = ['cart/update.js', { updates: Object.fromEntries(matches.map((item) => [item.key, 0])) }];
          } else if (quantity > 1) {
            action = ['cart/update.js', { updates: Object.fromEntries(matches.map((item, index) => [item.key, index === 0 ? 1 : 0])) }];
          } else if (eligible && !quantity && gift.auto_add_available !== false && gift.variant_ids.length === 1) {
            action = ['cart/add.js', { items: [{ id: gift.product_id, quantity: 1, properties: { Gift: 'Free Gift' } }] }];
          }
          if (action) break;
        }
        if (!action) {
          if (changed) {
            const items = document.querySelector('cart-drawer cart-items') || document.querySelector('cart-items');
            if (!items?.refresh) throw new Error('Cart refresh is unavailable');
            await items.refresh();
          }
          document.querySelector('.reward-gift-error')?.remove();
          return;
        }
        await this.request(...action);
        changed = true;
        cart = await this.request('cart.js');
      }
      throw new Error('Gift updates did not settle');
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { this.timer = null; this.run(); }, 80);
      this.updateNavigation();
    }
    async run() {
      this.pending = true;
      if (this.requests || this.intent) { this.updateNavigation(); return; }
      if (this.running) return;
      this.running = true;
      this.failed = false;
      this.updateNavigation();
      try {
        while (this.pending) {
          this.pending = false;
          await this.sync();
          if (this.requests || this.intent) break;
        }
      } catch (error) {
        this.failed = true;
        console.error('Gift sync failed:', error);
        const host = document.querySelector('drawer-gifts') || document.querySelector('cart-drawer');
        if (host && !host.querySelector('.reward-gift-error')) {
          const message = document.createElement('p');
          message.className = 'reward-gift-error';
          message.setAttribute('role', 'alert');
          message.textContent = 'Gift could not be added: ' + error.message;
          host.appendChild(message);
        }
      } finally { this.running = false; this.updateNavigation(); }
    }
    start() {
      const schedule = () => this.schedule();
      const style = document.createElement('style');
      style.textContent = 'cart-drawer [data-gift-navigation-lock] { opacity:.45!important; cursor:wait!important; }';
      document.head.appendChild(style);
      const block = event => {
        const control = event.target.closest?.('cart-drawer a[href], cart-drawer button[name="checkout"]');
        if (!control || !this.isBusy()) return;
        if (control.tagName === 'A' && !/\/cart\/?$/.test(new URL(control.href, location.href).pathname)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      document.addEventListener('click', block, true);
      document.addEventListener('auxclick', block, true);
      document.addEventListener('submit', event => {
        if (event.target.matches?.('#cart-drawer-form') && this.isBusy()) {
          event.preventDefault(); event.stopImmediatePropagation();
        }
      }, true);
      // Lock before the theme's debounced quantity handler starts its request.
      document.addEventListener('change', event => {
        if (event.target.matches?.('cart-items [name="updates[]"]')) {
          this.intent = true; this.updateNavigation();
        }
      }, true);
      // Track add/change/update requests made by the theme; our own gift
      // requests use originalFetch and remain covered by the running lock.
      this.originalFetch = window.fetch.bind(window);
      window.fetch = (...args) => {
        let mutation = false;
        try {
          const path = new URL(typeof args[0] === 'string' || args[0] instanceof URL ? args[0] : args[0].url, location.href).pathname;
          mutation = /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(path);
        } catch (_) {}
        if (!mutation) return this.originalFetch(...args);
        this.intent = false;
        this.requests = (this.requests || 0) + 1;
        this.updateNavigation();
        return this.originalFetch(...args).finally(() => {
          this.requests -= 1;
          schedule();
        });
      };
      ['on:cart:add', 'on:line-item:change', 'shopify:section:load'].forEach(name => document.addEventListener(name, schedule, true));
      window.addEventListener('pageshow', schedule);
      window.addEventListener('focus', schedule);
      const bind = () => {
        document.querySelectorAll('cart-items').forEach(items => {
          if (this.bound.has(items)) return;
          this.bound.add(items);
          // Original theme dispatches this event on the cart-items element.
          items.addEventListener('on:line-item:change', schedule);
        });
      };
      this.bound = new WeakSet();
      this.observer = new MutationObserver(records => {
        bind();
        this.updateNavigation();
        if (records.some(record => record.target.closest?.('cart-drawer, drawer-gifts, cart-items') ||
          Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches('cart-drawer, drawer-gifts, cart-items') || node.querySelector('drawer-gifts'))))) schedule();
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
      bind();
      schedule();
    }
  }
  window.cartGiftController = new CartGiftController();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => window.cartGiftController.start(), { once: true });
  else window.cartGiftController.start();
})();
